/**
 * AdminEventPage - Event management page with sidebar navigation
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { EventBroadcastAdmin } from '@/components/live/EventBroadcastAdmin';
import { eventAPI, adminDelegateAPI, checkinAPI, mealAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/api';
import { DashboardLayout } from '@/components/layouts';
import { cn } from '@/lib/utils';
import { getToken as sharedGetToken } from '@/lib/authToken';
import { useBulkPdfJob, BulkPdfProgress } from '@/hooks/useBulkPdfJob';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';

// Feature Components
import VirtualStage from '@/components/VirtualStage';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import HousingManager from '@/components/HousingManager';
import SessionManager from '@/components/SessionManager';
import SafetyCenter from '@/components/SafetyCenter';
import KioskMode from '@/components/KioskMode';
import AIResearchTool from '@/components/AIResearchTool';
import TaskBoard from '@/components/TaskBoard';
import ScavengerHunt from '@/components/ScavengerHunt';
import CommunicationsHub from '@/components/CommunicationsHub';
import VenueMaps from '@/components/VenueMaps';
import AuditLog from '@/components/AuditLog';
import AccessibilityAdminPanel from '@/components/AccessibilityAdminPanel';
import HousingAssignment from '@/components/HousingAssignment';
import SystemSettings from '@/components/SystemSettings';
import SponsorManagement from '@/components/SponsorManagement';
import ExhibitorBooths from '@/components/ExhibitorBooths';
import SpeakerManagement from '@/components/SpeakerManagement';
import SessionManagement from '@/components/SessionManagement';
import SurveyManagement from '@/components/SurveyManagement';
import PrizeManagement from '@/components/PrizeManagement';
import CertificateManagement from '@/components/CertificateManagement';
import DigitalCredentials from '@/components/DigitalCredentials';
import WaitlistManagement from '@/components/WaitlistManagement';
import PromoCodeManagement from '@/components/PromoCodeManagement';
import QuickActions from '@/components/QuickActions';
import RoleManagement from '@/components/RoleManagement';
import TeamManagement from '@/components/TeamManagement';
import AdminLayout from '@/components/AdminLayout';
import RegistrationProgressTracker from '@/components/RegistrationProgressTracker';
// Note: School module is now standalone at /admin/schools
import PaymentManagement from '@/components/PaymentManagement';
import RevenueDashboard from '@/components/RevenueDashboard';
import SystemDiagnostics from '@/components/SystemDiagnostics';
import SampleDataGenerator from '@/components/SampleDataGenerator';
import RealtimeStats from '@/components/RealtimeStats';
import LiveEventDashboard from '@/components/LiveEventDashboard';
import ProvincialDashboard from '@/components/ProvincialDashboard';
import BreakoutSessionsManager from '@/components/BreakoutSessionsManager';
import VirtualStageManager from '@/components/VirtualStageManager';
import PaymentGatewayManager from '@/components/PaymentGatewayManager';
import MealManagement from '@/components/MealManagement';
import MealScanner from '@/components/MealScanner';
import AISettings from '@/components/AISettings';
import SessionAIPanel from '@/components/SessionAIPanel';
import TicketTierManager from '@/components/TicketTierManager';
import NetworkingAppointments from '@/components/NetworkingAppointments';
import SecurityManagement from '@/components/SecurityManagement';
import AddOnsManager from '@/components/AddOnsManager';
import PaymentPlanManager from '@/components/PaymentPlanManager';
import DelegateAccessManager from '@/components/DelegateAccessManager';
import CustomFieldsManager from '@/components/CustomFieldsManager';
import FinanceManager from '@/components/FinanceManager';
import DelegatePayments from '@/components/DelegatePayments';
import StockScanner from '@/components/StockScanner';
import EventStockManagement from '@/components/EventStockManagement';
import DelegateEditor from '@/components/DelegateEditor';
import DelegateQRDisplay from '@/components/DelegateQRDisplay';
import QRHealthCheck from '@/components/QRHealthCheck';
import BadgeDesigner from '@/components/BadgeDesigner';
import CampusAccessControl from '@/components/CampusAccessControl';
import VehicleManagement from '@/components/VehicleManagement';
import VisitorPassManagement from '@/components/VisitorPassManagement';
import PreRegistrationInvites from '@/components/PreRegistrationInvites';
import NetworkCameraSettings from '@/components/NetworkCameraSettings';
import BulkDelegateImport from '@/components/BulkDelegateImport';
import BibleChallengesManagement from '@/components/BibleChallengesManagement';
import ScopedBiblePlans from '@/components/bible/ScopedBiblePlans';
import POSManager from '@/components/POSManager';
import QRBatchExport from '@/components/QRBatchExport';
import SecurityDashboard from '@/components/SecurityDashboard';
import EventFeatureToggles from '@/components/EventFeatureToggles';
import EventOrgTags from '@/components/event_admin/EventOrgTags';

// Icons
import {
  Calendar, Users, MapPin, AlertCircle, Loader2, Check, CheckCircle2, XCircle,
  ScanLine, Maximize, Utensils, Coins, Camera, QrCode, FileDown, Download, Wifi, CreditCard,
  UserPlus, DollarSign, Clock, Globe, FileSpreadsheet, Pencil, Receipt, Home, Star, GraduationCap,
  Ticket, CheckSquare, Square, Trash2, AlertTriangle, Repeat, Plus
} from 'lucide-react';
import { isConfirmed, isAwaitingCheckIn, matchesStatusFilter } from '@/lib/delegateStatus';

// ============== Edit Event Details ==============
/**
 * iter 632 — the Event Details card was read-only text. `PUT /events/{id}`
 * has accepted `start_date`, `end_date`, `event_type`, `venue`, `timezone`,
 * `status` and `visibility` all along, and nothing in the product sent any of
 * them: the only caller of `eventAPI.update` was `CustomFieldsManager`, posting
 * a `settings` key the model did not declare and therefore discarded. So a date
 * could not be corrected and an event could not be switched between in-person
 * and online without going at the database directly.
 *
 * `event_type` is not just a label — `in_person | online | hybrid` is what the
 * venue line and the joining instructions key off, so it is the first field
 * here rather than buried under the toggles.
 */
const EVENT_TYPES = [
  { value: 'in_person', label: 'In person' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid (both)' },
];

/** `venue` is stored as a plain string on newer events and as an object on
 *  older ones. Read whichever it is, and write back the same shape so an edit
 *  never reshapes a record that other screens read as an object. */
const venueText = (venue) =>
  (venue && typeof venue === 'object' ? (venue.name || venue.address || '') : (venue || ''));

/** `<input type="date">` wants exactly `YYYY-MM-DD`. */
const dateInputValue = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

// iter 635 — repeat rules. The server accepts the same four values; the leap
// day policy is stated on the series card so nobody has to guess what happens
// to 29 February.
const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'yearly', label: 'Every year' },
];

const spanDaysOf = (from, to) => {
  if (!from || !to) return 1;
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 1;
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
};

const recurrenceHint = (form) => {
  const span = spanDaysOf(form.start_date, form.end_date);
  const every = { weekly: 'week', monthly: 'month', yearly: 'year' }[form.recurrence];
  const run = span > 1 ? `, ${span} days each time` : '';
  const ends = form.recurrence_until ? ` until ${form.recurrence_until}` : ', with no end date';
  return `Repeats every ${every}${run}${ends}. Each run is managed separately below.`;
};

/** A date as `Sat 10 Oct 2026`, in the reader's locale, without a UTC shift. */
const prettyDay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString(undefined,
    { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

/** "8–11 October 2026" or a single day. */
const prettyRange = (from, to) => {
  if (!from) return 'No dates set';
  if (!to || to === from) return prettyDay(from);
  return `${prettyDay(from)} — ${prettyDay(to)}`;
};

const EditEventDetailsDialog = ({ event, open, onClose }) => {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: event.name || '',
    description: event.description || '',
    start_date: dateInputValue(event.start_date),
    end_date: dateInputValue(event.end_date),
    event_type: event.event_type || 'in_person',
    venue: venueText(event.venue),
    timezone: event.timezone || 'UTC',
    status: event.status || 'draft',
    visibility: event.visibility || 'public',
    recurrence: event.recurrence || 'none',
    recurrence_until: dateInputValue(event.recurrence_until),
  });

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('An event needs a name.');
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      // The backend refuses this too (`EventUpdate._ordered`); saying so here
      // costs nothing and saves a round trip.
      toast.error('The end date cannot be before the start date.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        event_type: form.event_type,
        timezone: form.timezone,
        status: form.status,
        visibility: form.visibility,
        recurrence: form.recurrence,
        recurrence_until: form.recurrence === 'none' ? '' : form.recurrence_until,
      };
      // Only send a date the person actually set. The handler drops nulls, so
      // an empty string would be stored as an empty date rather than ignored.
      if (form.start_date) payload.start_date = form.start_date;
      if (form.end_date) payload.end_date = form.end_date;
      // Write the venue back in the shape this record already uses.
      if (event.venue && typeof event.venue === 'object') {
        payload.venue = { ...event.venue, name: form.venue };
      } else {
        payload.venue = form.venue;
      }

      await eventAPI.update(event.id, payload);
      await queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      toast.success('Event details updated');
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not update the event'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit event details</DialogTitle>
          <DialogDescription>
            Dates, format and venue. Feature switches stay on the Settings tab.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="edit-event-name">Event name</Label>
            <Input
              id="edit-event-name"
              data-testid="edit-event-name"
              value={form.name}
              onChange={(e) => set('name')(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="edit-event-type">Format</Label>
            <Select value={form.event_type} onValueChange={set('event_type')}>
              <SelectTrigger id="edit-event-type" data-testid="edit-event-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-event-start">Starts</Label>
              <Input
                id="edit-event-start"
                data-testid="edit-event-start"
                type="date"
                value={form.start_date}
                onChange={(e) => set('start_date')(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="edit-event-end">Ends</Label>
              <Input
                id="edit-event-end"
                data-testid="edit-event-end"
                type="date"
                value={form.end_date}
                onChange={(e) => set('end_date')(e.target.value)}
              />
            </div>
          </div>

          {form.event_type !== 'online' && (
            <div>
              <Label htmlFor="edit-event-venue">Venue</Label>
              <Input
                id="edit-event-venue"
                data-testid="edit-event-venue"
                value={form.venue}
                onChange={(e) => set('venue')(e.target.value)}
                placeholder="Where it is being held"
              />
            </div>
          )}

          <div>
            <Label htmlFor="edit-event-timezone">Time zone</Label>
            <Input
              id="edit-event-timezone"
              data-testid="edit-event-timezone"
              value={form.timezone}
              onChange={(e) => set('timezone')(e.target.value)}
              placeholder="e.g. Africa/Harare"
            />
          </div>

          {/* iter 635 — the annual conference. One event with a rule, not a
              copy per year; occurrences are managed on the card below. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-event-recurrence">Repeats</Label>
              <Select value={form.recurrence} onValueChange={set('recurrence')}>
                <SelectTrigger id="edit-event-recurrence" data-testid="edit-event-recurrence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECURRENCE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-event-recurrence-until">Until (optional)</Label>
              <Input
                id="edit-event-recurrence-until"
                data-testid="edit-event-recurrence-until"
                type="date"
                value={form.recurrence_until}
                onChange={(e) => set('recurrence_until')(e.target.value)}
                disabled={form.recurrence === 'none'}
                min={form.start_date || undefined}
              />
            </div>
          </div>
          {form.recurrence !== 'none' && (
            <p className="text-xs text-slate-500" data-testid="edit-event-recurrence-hint">
              {recurrenceHint(form)}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-event-status">Status</Label>
              <Select value={form.status} onValueChange={set('status')}>
                <SelectTrigger id="edit-event-status" data-testid="edit-event-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-event-visibility">Visibility</Label>
              <Select value={form.visibility} onValueChange={set('visibility')}>
                <SelectTrigger id="edit-event-visibility" data-testid="edit-event-visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="edit-event-description">Description</Label>
            <Textarea
              id="edit-event-description"
              data-testid="edit-event-description"
              rows={3}
              value={form.description}
              onChange={(e) => set('description')(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="edit-event-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== Series occurrences & daily programme ==============
/**
 * iter 635 — a yearly conference generates its runs from a rule. Nothing is
 * written per year until somebody opens that year for registration, and what
 * is written then is a separate event: its own delegates, payments, housing,
 * dining and check-in. Registering for 2026 does not register you for 2027.
 */
const OccurrenceSeriesCard = ({ event }) => {
  const queryClient = useQueryClient();
  const [busyDay, setBusyDay] = useState('');
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['event-occurrences', event.id],
    queryFn: () => eventAPI.occurrences(event.id, { limit: 12 }).then((r) => r.data),
    enabled: !!event.id,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['event-occurrences', event.id] });
    await queryClient.invalidateQueries({ queryKey: ['event', event.id] });
  };

  const run = async (day, action, message) => {
    setBusyDay(day);
    try {
      const response = await action();
      toast.success(message(response?.data));
      await refresh();
      return response;
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not update that occurrence'));
      return null;
    } finally {
      setBusyDay('');
    }
  };

  if ((event.recurrence || 'none') === 'none' && !event.series_id) return null;

  return (
    <Card data-testid="event-series-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Repeat className="h-4 w-4" /> Repeats
        </CardTitle>
        <CardDescription>
          {event.series_id
            ? 'This is one occurrence of a repeating event.'
            : data?.leap_day_rule}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && <p className="text-sm text-slate-500">Working out the dates…</p>}
        {(data?.occurrences || []).map((occurrence) => {
          const busy = busyDay === occurrence.start_date;
          return (
            <div
              key={occurrence.start_date}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2"
              data-testid={`occurrence-row-${occurrence.start_date}`}
            >
              <div className="min-w-0">
                <p className={`text-sm ${occurrence.cancelled ? 'line-through text-slate-400' : ''}`}>
                  {prettyRange(occurrence.start_date, occurrence.end_date)}
                </p>
                <p className="text-xs text-slate-500">
                  {occurrence.span_days > 1 ? `${occurrence.span_days} days` : 'One day'}
                  {occurrence.is_first && ' · first run'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {occurrence.cancelled && <Badge variant="outline">Cancelled</Badge>}
                {occurrence.activated_event_id && <Badge>Open for registration</Badge>}
                {occurrence.cancelled ? (
                  <Button
                    size="sm" variant="outline" disabled={busy}
                    data-testid={`occurrence-restore-${occurrence.start_date}`}
                    onClick={() => run(occurrence.start_date,
                      () => eventAPI.restoreOccurrence(event.id, occurrence.start_date),
                      () => 'Occurrence restored')}
                  >
                    Restore
                  </Button>
                ) : (
                  <>
                    {!occurrence.activated_event_id && !occurrence.is_first && (
                      <Button
                        size="sm" variant="outline" disabled={busy}
                        data-testid={`occurrence-activate-${occurrence.start_date}`}
                        onClick={async () => {
                          const response = await run(occurrence.start_date,
                            () => eventAPI.activateOccurrence(event.id, occurrence.start_date),
                            () => 'Opened as its own event — registrations start empty');
                          if (response?.data?.event_id) {
                            navigate(`/admin/event/${response.data.event_id}`);
                          }
                        }}
                      >
                        Open for registration
                      </Button>
                    )}
                    {occurrence.activated_event_id && (
                      <Button
                        size="sm" variant="outline"
                        data-testid={`occurrence-open-${occurrence.start_date}`}
                        onClick={() => navigate(`/admin/event/${occurrence.activated_event_id}`)}
                      >
                        Manage
                      </Button>
                    )}
                    {!occurrence.is_first && !occurrence.activated_event_id && (
                      <Button
                        size="sm" variant="ghost" disabled={busy}
                        data-testid={`occurrence-cancel-${occurrence.start_date}`}
                        onClick={() => run(occurrence.start_date,
                          () => eventAPI.cancelOccurrence(event.id, occurrence.start_date),
                          () => 'Occurrence cancelled — the series continues')}
                      >
                        Cancel
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        {!isLoading && !(data?.occurrences || []).length && (
          <p className="text-sm text-slate-500">No upcoming occurrences.</p>
        )}
      </CardContent>
    </Card>
  );
};

/** The per-day schedule of a conference: Friday 17:00-21:00, Saturday 12:00-21:00. */
const DailyProgrammeCard = ({ event }) => {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState(() => (event.programme || []).map((r) => ({ ...r })));
  const [saving, setSaving] = useState(false);

  const dayOptions = useMemo(() => {
    const from = dateInputValue(event.start_date);
    const to = dateInputValue(event.end_date) || from;
    if (!from) return [];
    const out = [];
    for (let cursor = new Date(`${from}T00:00:00`), guard = 0;
         cursor <= new Date(`${to}T00:00:00`) && guard < 40;
         cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1), guard += 1) {
      out.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`);
    }
    return out;
  }, [event.start_date, event.end_date]);

  const update = (index, key, value) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)));

  const save = async () => {
    setSaving(true);
    try {
      await eventAPI.update(event.id, { programme: rows });
      await queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      toast.success('Programme saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save the programme'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="event-programme-card">
      <CardHeader>
        <CardTitle>Daily programme</CardTitle>
        <CardDescription>
          One row per day, for a conference that runs to a different timetable each day.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-12 gap-2 items-end" data-testid={`programme-row-${index}`}>
            <div className="col-span-4">
              <Label className="text-xs">Day</Label>
              <select
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                value={row.date || ''}
                onChange={(e) => update(index, 'date', e.target.value)}
                data-testid={`programme-date-${index}`}
              >
                <option value="">Pick a day</option>
                {dayOptions.map((day) => (
                  <option key={day} value={day}>{prettyDay(day)}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">From</Label>
              <Input type="time" value={row.start_time || ''} data-testid={`programme-start-${index}`}
                     onChange={(e) => update(index, 'start_time', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">To</Label>
              <Input type="time" value={row.end_time || ''} data-testid={`programme-end-${index}`}
                     onChange={(e) => update(index, 'end_time', e.target.value)} />
            </div>
            <div className="col-span-3">
              <Label className="text-xs">What is happening</Label>
              <Input value={row.title || ''} placeholder="Opening session" data-testid={`programme-title-${index}`}
                     onChange={(e) => update(index, 'title', e.target.value)} />
            </div>
            <div className="col-span-1">
              <Button variant="ghost" size="sm" data-testid={`programme-remove-${index}`}
                      onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {!rows.length && (
          <p className="text-sm text-slate-500">
            Nothing scheduled yet. Add a row for each day of the event.
          </p>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" data-testid="programme-add-row"
            onClick={() => setRows((prev) => [...prev, {
              date: dayOptions[Math.min(prev.length, dayOptions.length - 1)] || '',
              start_time: '', end_time: '', title: '',
            }])}
          >
            <Plus className="h-4 w-4 mr-2" /> Add a day
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="programme-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save programme'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Admin Event Overview ==============
const AdminEventOverview = ({ event, delegates, tiers, eventSlug, onTabChange }) => {
  const [editingDetails, setEditingDetails] = useState(false);
  const checkedIn = delegates.filter((d) => d.status === "checked_in").length;
  // Was `d.status === "confirmed"`, a value nothing writes — the tile read 0
  // on every event. This is what the label says: holding a place, not yet in.
  const confirmed = delegates.filter((d) => isAwaitingCheckIn(d.status)).length;
  const totalCapacity = tiers?.reduce((acc, t) => acc + (t.capacity || 0), 0) || 0;
  // Use actual delegate count instead of tier's sold count (which may not be updated)
  const totalSold = delegates.length;
  
  // Calculate actual tier counts from delegates
  const tierCounts = delegates.reduce((acc, d) => {
    const tierId = d.tier_id || 'no_tier';
    acc[tierId] = (acc[tierId] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <QuickActions eventId={event.id} eventSlug={eventSlug} onAction={onTabChange} />
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{totalSold}</div>
            <div className="text-sm text-slate-500">Total Registrations</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{checkedIn}</div>
            <div className="text-sm text-slate-500">Checked In</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{confirmed}</div>
            <div className="text-sm text-slate-500">Pending Check-In</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{totalCapacity - totalSold}</div>
            <div className="text-sm text-slate-500">Spots Available</div>
          </CardContent>
        </Card>
      </div>

      {/* Registration Progress Tracker */}
      <RegistrationProgressTracker event={event} delegates={delegates} tiers={tiers || []} />

      {/* Tiers */}
      <Card>
        <CardHeader>
          <CardTitle>Ticket Tiers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tiers?.map((tier) => {
              const actualSold = tierCounts[tier.id] || 0;
              return (
                <div key={tier.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <div className="font-medium">{tier.name}</div>
                    <div className="text-sm text-slate-500">${tier.price}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{actualSold} / {tier.capacity}</div>
                    <Progress value={(actualSold / tier.capacity) * 100} className="w-32 h-2" />
                  </div>
                </div>
              );
            })}
            {/* Show delegates without tier if any */}
            {tierCounts['no_tier'] > 0 && (
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                <div>
                  <div className="font-medium">No Tier Assigned</div>
                  <div className="text-sm text-amber-600">Needs tier assignment</div>
                </div>
                <div className="text-right">
                  <div className="font-medium">{tierCounts['no_tier']}</div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Event Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Event Details</CardTitle>
          {/* iter 632 — this card had no way to change anything on it. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditingDetails(true)}
            data-testid="edit-event-details"
          >
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            {/* An event with no dates yet rendered "Invalid Date - Invalid Date". */}
            <span data-testid="event-date-range">
              {prettyRange(dateInputValue(event.start_date), dateInputValue(event.end_date))}
              {spanDaysOf(dateInputValue(event.start_date), dateInputValue(event.end_date)) > 1
                && ` · ${spanDaysOf(dateInputValue(event.start_date), dateInputValue(event.end_date))} days`}
            </span>
          </div>
          {(event.recurrence || 'none') !== 'none' && (
            <div className="flex items-center gap-2" data-testid="event-recurrence-line">
              <Repeat className="h-4 w-4 text-slate-500" />
              <span>
                {(RECURRENCE_OPTIONS.find((r) => r.value === event.recurrence) || {}).label}
                {event.recurrence_until ? ` until ${prettyDay(event.recurrence_until)}` : ''}
              </span>
            </div>
          )}
          {/* iter 632 — the format was never shown, so there was no way to tell
              an online event from an in-person one on its own admin page. */}
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-slate-500" />
            <span>
              {(EVENT_TYPES.find((t) => t.value === (event.event_type || 'in_person')) || {}).label
                || event.event_type}
            </span>
          </div>
          {/* `venue` is a string on newer events and an object on older ones;
              the old `event.venue?.name` showed nothing at all for a string. */}
          {venueText(event.venue) && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-500" />
              <span>{venueText(event.venue)}</span>
            </div>
          )}
          {event.timezone && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-500" />
              <span>{event.timezone}</span>
            </div>
          )}
          <div className="flex gap-2 mt-4 flex-wrap">
            {event.status && <Badge variant="outline">{event.status}</Badge>}
            {event.meals_enabled && <Badge>Meals Enabled</Badge>}
            {event.housing_enabled && <Badge>Housing Enabled</Badge>}
            {event.economy_enabled && <Badge>Economy Enabled</Badge>}
          </div>
        </CardContent>
      </Card>

      {editingDetails && (
        <EditEventDetailsDialog
          event={event}
          open={editingDetails}
          onClose={() => setEditingDetails(false)}
        />
      )}

      <OccurrenceSeriesCard event={event} />
      <DailyProgrammeCard event={event} />
    </div>
  );
};

// ============== Admin Delegate List ==============
const AdminDelegateList = ({ eventId, delegates }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDelegate, setSelectedDelegate] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [showManualRegister, setShowManualRegister] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingDelegate, setEditingDelegate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  
  // Bulk selection state
  const [selectedDelegates, setSelectedDelegates] = useState([]);
  const [showBulkTierDialog, setShowBulkTierDialog] = useState(false);
  const [showQRExport, setShowQRExport] = useState(false);
  const [bulkTierId, setBulkTierId] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkSendNotification, setBulkSendNotification] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  
  // Bulk delete state
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeletePassword, setBulkDeletePassword] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState('');
  
  // Badge download options state
  const [showBadgeOptionsDialog, setShowBadgeOptionsDialog] = useState(false);
  const [badgePageSize, setBadgePageSize] = useState('A4');
  const [badgesPerPage, setBadgesPerPage] = useState('8');
  const bulkPdfJob = useBulkPdfJob();
  const isDownloadingBadges = ['preparing', 'running'].includes(bulkPdfJob.status);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const queryClient = useQueryClient();

  // Form state for manual registration
  const [manualForm, setManualForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    gender: '',
    tier_id: 'free',
    payment_method: 'cash',
    amount_paid: 0,
    apply_late_fee: false,
    custom_late_fee: null,
    notes: '',
    receipt_number: '',
    dietary_requirements: '',
    meal_access: true,
    accommodation_access: false
  });

  // Fetch tiers for the event
  const { data: tiers } = useQuery({
    queryKey: ['tiers', eventId],
    queryFn: () => eventAPI.getTiers(eventId).then(r => r.data),
  });

  // Fetch late registration settings
  const { data: lateSettings } = useQuery({
    queryKey: ['late-settings', eventId],
    queryFn: async () => {
      const token = sharedGetToken();
      const res = await fetch(`${API_URL}/api/events/${eventId}/settings/late-registration`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return res.json();
    },
  });

  // Calculate total amount based on selections
  const calculateTotal = () => {
    let base = 0;
    if (manualForm.tier_id && manualForm.tier_id !== 'free') {
      const tier = tiers?.find(t => t.id === manualForm.tier_id);
      base = tier?.price || 0;
    }
    
    let lateFee = 0;
    if (manualForm.apply_late_fee) {
      if (manualForm.custom_late_fee !== null && manualForm.custom_late_fee !== '') {
        lateFee = parseFloat(manualForm.custom_late_fee) || 0;
      } else if (lateSettings?.enabled) {
        if (lateSettings.fee_type === 'percentage') {
          lateFee = base * (lateSettings.fee_value / 100);
        } else {
          lateFee = lateSettings.fee_value || 0;
        }
      }
    }
    
    return { base, lateFee, total: base + lateFee };
  };

  // Bulk tier assignment handler
  const handleBulkTierAssign = async () => {
    if (!bulkTierId || selectedDelegates.length === 0) {
      toast.error('Please select a tier and at least one delegate');
      return;
    }

    setIsBulkProcessing(true);
    try {
      const token = sharedGetToken();
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/delegates/bulk-assign-tier`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          delegate_ids: selectedDelegates,
          tier_id: bulkTierId,
          notes: bulkNotes,
          send_notification: bulkSendNotification
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(
          <div>
            <p className="font-semibold">Bulk tier assignment complete!</p>
            <p className="text-sm">{data.summary?.successful || 0} successful, {data.summary?.failed || 0} failed, {data.summary?.skipped || 0} skipped</p>
          </div>,
          { duration: 5000 }
        );
        setShowBulkTierDialog(false);
        setSelectedDelegates([]);
        setBulkTierId('');
        setBulkNotes('');
        setBulkSendNotification(false);
        queryClient.invalidateQueries({ queryKey: ['delegates', eventId] });
      } else {
        toast.error(data.detail || 'Bulk assignment failed');
      }
    } catch (err) {
      toast.error('Failed to assign tiers');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  // Toggle delegate selection
  const toggleDelegateSelection = (delegateId) => {
    setSelectedDelegates(prev => 
      prev.includes(delegateId)
        ? prev.filter(id => id !== delegateId)
        : [...prev, delegateId]
    );
  };

  // Select all filtered delegates
  const selectAllFiltered = () => {
    const filteredIds = filtered.map(d => d.id);
    const allSelected = filteredIds.every(id => selectedDelegates.includes(id));
    if (allSelected) {
      setSelectedDelegates(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedDelegates(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  // Select only interested delegates
  const selectInterestedOnly = () => {
    const interestedIds = delegates.filter(d => d.status === 'interested').map(d => d.id);
    setSelectedDelegates(interestedIds);
    if (interestedIds.length > 0) {
      toast.success(`Selected ${interestedIds.length} interested delegates`);
    } else {
      toast.info('No interested delegates found');
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedDelegates.length === 0) {
      toast.error('No delegates selected');
      return;
    }
    
    if (!bulkDeletePassword) {
      setBulkDeleteError('Password is required');
      return;
    }
    
    setIsBulkDeleting(true);
    setBulkDeleteError('');
    
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/events/${eventId}/delegates/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          delegate_ids: selectedDelegates,
          admin_password: bulkDeletePassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(
          <div>
            <p className="font-semibold">Bulk Delete Complete</p>
            <p className="text-sm">{data.deleted_count} delegate(s) deleted</p>
          </div>,
          { duration: 5000 }
        );
        setShowBulkDeleteDialog(false);
        setSelectedDelegates([]);
        setBulkDeletePassword('');
        queryClient.invalidateQueries({ queryKey: ['delegates', eventId] });
      } else if (response.status === 401) {
        setBulkDeleteError('Invalid password. Please try again.');
      } else if (response.status === 403) {
        setBulkDeleteError('Only super admins can perform bulk delete.');
      } else {
        setBulkDeleteError(data.detail || 'Bulk delete failed');
      }
    } catch (err) {
      console.error('Bulk delete error:', err);
      setBulkDeleteError('Failed to delete delegates. Please try again.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleManualRegister = async () => {
    if (!manualForm.name || !manualForm.email) {
      toast.error('Name and email are required');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = sharedGetToken();
      const response = await fetch(`${API_URL}/api/events/${eventId}/delegates/manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...manualForm,
          custom_late_fee: manualForm.custom_late_fee ? parseFloat(manualForm.custom_late_fee) : null
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(
          <div>
            <p className="font-semibold">Delegate registered successfully!</p>
            <p className="text-sm">Temp password: <code className="bg-slate-100 px-1 rounded">{data.temp_password}</code></p>
            <p className="text-xs text-slate-500 mt-1">Share this with the delegate for login</p>
          </div>,
          { duration: 10000 }
        );
        setShowManualRegister(false);
        setManualForm({
          name: '', email: '', phone: '', company: '', title: '',
          tier_id: 'free', payment_method: 'cash', amount_paid: 0,
          apply_late_fee: false, custom_late_fee: null, notes: ''
        });
        queryClient.invalidateQueries({ queryKey: ['delegates', eventId] });
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (err) {
      toast.error('Failed to register delegate');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manual check-in state
  const [checkingIn, setCheckingIn] = useState(null);
  const [showUndoConfirm, setShowUndoConfirm] = useState(null);
  const [undoingCheckIn, setUndoingCheckIn] = useState(null);

  // Manual check-in handler
  const handleManualCheckIn = async (delegateId, delegateName) => {
    setCheckingIn(delegateId);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/events/${eventId}/delegates/${delegateId}/manual-checkin?gate=admin-desk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(
          <div>
            <p className="font-semibold">✓ Checked In Successfully</p>
            <p className="text-sm">{delegateName}</p>
          </div>
        );
        queryClient.invalidateQueries({ queryKey: ['delegates', eventId] });
      } else if (data.error === 'already_checked_in') {
        toast.info(`${delegateName} is already checked in`);
      } else {
        toast.error(data.message || 'Check-in failed');
      }
    } catch (err) {
      toast.error('Failed to check in delegate');
    } finally {
      setCheckingIn(null);
    }
  };

  // Undo check-in handler
  const handleUndoCheckIn = async (delegateId, delegateName) => {
    setUndoingCheckIn(delegateId);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/events/${eventId}/delegates/${delegateId}/undo-checkin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Check-in undone for ${delegateName}`);
        queryClient.invalidateQueries({ queryKey: ['delegates', eventId] });
        setShowUndoConfirm(null);
      } else {
        toast.error(data.message || 'Failed to undo check-in');
      }
    } catch (err) {
      toast.error('Failed to undo check-in');
    } finally {
      setUndoingCheckIn(null);
    }
  };

  const filtered = delegates.filter((d) => {
    const matchSearch =
      d.name?.toLowerCase().includes(search.toLowerCase()) ||
      d.first_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.last_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = matchesStatusFilter(d.status, statusFilter);
    return matchSearch && matchStatus;
  });

  // Helper function to get auth token consistently
  const getToken = () => sharedGetToken();

  const downloadBadge = async (delegateId, firstName, lastName) => {
    try {
      const token = getToken();
      if (!token) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      toast.info('Generating badge PDF...');
      
      // Download actual PDF badge
      const response = await fetch(
        `${API_URL}/api/events/${eventId}/delegates/${delegateId}/badge-pdf-custom`,
        { 
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({}) // Use default design
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const name = `${firstName || ''}${lastName ? '_' + lastName : ''}`.trim() || 'delegate';
        a.download = `badge_${name.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Badge PDF downloaded!');
      } else if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (response.status === 404) {
        toast.error('Badge endpoint not found. The delegate may not exist.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to generate badge PDF');
      }
    } catch (err) {
      console.error('Badge download error:', err);
      toast.error('Failed to download badge. Please check your connection.');
    }
  };

  // Download all badges as PDF with options (uses shared bulk-pdf hook — inline
  // for small batches, background job with progress for large batches).
  const handleDownloadAllBadges = async () => {
    const token = getToken();
    if (!token) {
      toast.error('Session expired. Please log in again.');
      return;
    }
    const recordCount = (delegates || []).length;
    const date = new Date().toISOString().split('T')[0];
    bulkPdfJob.start({
      asyncEndpoint: `${API_URL}/api/events/${eventId}/badges/bulk-pdf/async`,
      inlineEndpoint: `${API_URL}/api/events/${eventId}/badges/bulk-pdf`,
      body: {
        page_size: badgePageSize,
        badges_per_page: parseInt(badgesPerPage, 10),
      },
      recordCount,
      filename: `badges_all_${badgePageSize}_${date}.pdf`,
      autoDownload: true,
    });
  };

  const fees = calculateTotal();

  return (
    <div className="space-y-4">
      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-0 sm:min-w-[200px]">
          <Input
            placeholder="Search delegates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="checked_in">Checked In</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="interested">Interested</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Badge Download with Options Dialog */}
        <Dialog open={showBadgeOptionsDialog} onOpenChange={setShowBadgeOptionsDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="download-badges-btn">
              <Download className="h-4 w-4 mr-2" />
              Download All Badges
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Badge Download Options
              </DialogTitle>
              <DialogDescription>
                Configure how badges are arranged on the PDF
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Page Size</Label>
                <Select value={badgePageSize} onValueChange={setBadgePageSize}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select page size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A3">A3 (297×420mm) - Large</SelectItem>
                    <SelectItem value="A4">A4 (210×297mm) - Standard</SelectItem>
                    <SelectItem value="A5">A5 (148×210mm) - Small</SelectItem>
                    <SelectItem value="Letter">Letter (8.5×11")</SelectItem>
                    <SelectItem value="Legal">Legal (8.5×14")</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Badges Per Page</Label>
                <Select value={badgesPerPage} onValueChange={setBadgesPerPage}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select badges per page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 badge (full page)</SelectItem>
                    <SelectItem value="2">2 badges</SelectItem>
                    <SelectItem value="4">4 badges (2×2)</SelectItem>
                    <SelectItem value="6">6 badges (2×3)</SelectItem>
                    <SelectItem value="8">8 badges (2×4)</SelectItem>
                    <SelectItem value="9">9 badges (3×3)</SelectItem>
                    <SelectItem value="12">12 badges (3×4)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded-md">
                <p><strong>Total delegates:</strong> {delegates.length}</p>
                <p><strong>Pages needed:</strong> {Math.ceil(delegates.length / parseInt(badgesPerPage))}</p>
              </div>
            </div>
            <BulkPdfProgress
              status={bulkPdfJob.status}
              progress={bulkPdfJob.progress}
              error={bulkPdfJob.error}
              filename={bulkPdfJob.filename}
              onDownload={bulkPdfJob.manualDownload}
              onReset={bulkPdfJob.reset}
              className="mt-2"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBadgeOptionsDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleDownloadAllBadges} disabled={isDownloadingBadges} data-testid="bulk-badges-generate-btn">
                {isDownloadingBadges ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* CSV Export Button */}
        <Button 
          variant="outline" 
          data-testid="export-csv-btn"
          onClick={async () => {
            try {
              const token = getToken();
              if (!token) {
                toast.error('Session expired. Please log in again.');
                return;
              }
              toast.info('Generating CSV export...');
              const response = await fetch(`${API_URL}/api/events/${eventId}/delegates/export/csv`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `delegates_export_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success('CSV downloaded successfully!');
              } else if (response.status === 401) {
                toast.error('Session expired. Please log in again.');
              } else if (response.status === 404) {
                toast.error('No delegates found to export.');
              } else {
                const errorData = await response.json().catch(() => ({}));
                toast.error(errorData.detail || 'Failed to export CSV');
              }
            } catch (err) {
              console.error('CSV export error:', err);
              toast.error('Failed to export CSV. Please check your connection.');
            }
          }}
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
        
        {/* Bulk Import Button */}
        <BulkDelegateImport eventId={eventId} />

        {/* QR Code Export Button */}
        <Button 
          variant="outline"
          onClick={() => setShowQRExport(true)}
          data-testid="qr-export-btn"
        >
          <QrCode className="h-4 w-4 mr-2" />
          Export QR Codes
        </Button>

        {/* QR Export Modal */}
        <QRBatchExport 
          eventId={eventId}
          eventName={event?.name}
          isOpen={showQRExport}
          onClose={() => setShowQRExport(false)}
        />

        {/* Bulk Tier Assignment Button */}
        <Dialog open={showBulkTierDialog} onOpenChange={setShowBulkTierDialog}>
          <DialogTrigger asChild>
            <Button 
              variant="outline"
              disabled={selectedDelegates.length === 0}
              className={selectedDelegates.length > 0 ? "border-primary text-primary" : ""}
              data-testid="bulk-tier-assign-btn"
            >
              <Ticket className="h-4 w-4 mr-2" />
              Assign Tier ({selectedDelegates.length})
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Bulk Tier Assignment
              </DialogTitle>
              <DialogDescription>
                Assign a ticket tier to {selectedDelegates.length} selected delegate{selectedDelegates.length !== 1 ? 's' : ''}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Selected delegates summary */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <div className="font-medium text-blue-800">
                  {selectedDelegates.length} delegate{selectedDelegates.length !== 1 ? 's' : ''} selected
                </div>
                <div className="text-blue-600 text-xs mt-1">
                  {filtered.filter(d => selectedDelegates.includes(d.id) && d.status === 'interested').length} interested, {' '}
                  {filtered.filter(d => selectedDelegates.includes(d.id) && d.status !== 'interested').length} other
                </div>
              </div>

              <div className="space-y-2">
                <Label>Select Tier *</Label>
                <Select value={bulkTierId} onValueChange={setBulkTierId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiers?.map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>
                        <div className="flex justify-between items-center w-full gap-4">
                          <span>{tier.name}</span>
                          <span className="text-slate-500">${tier.price}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {bulkTierId && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Tier Price:</span>
                    <span className="font-medium">
                      ${tiers?.find(t => t.id === bulkTierId)?.price?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Each delegate will owe this amount (minus any existing payments)
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={bulkNotes}
                  onChange={(e) => setBulkNotes(e.target.value)}
                  placeholder="Bulk upgrade from interest registrations..."
                  rows={2}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="bulk_send_notification"
                  checked={bulkSendNotification}
                  onCheckedChange={setBulkSendNotification}
                />
                <Label htmlFor="bulk_send_notification" className="text-sm">
                  Send notification email to delegates
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkTierDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkTierAssign}
                disabled={!bulkTierId || isBulkProcessing}
              >
                {isBulkProcessing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Ticket className="h-4 w-4 mr-2" />
                )}
                Assign to {selectedDelegates.length} Delegate{selectedDelegates.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Bulk Delete Button */}
        <Dialog open={showBulkDeleteDialog} onOpenChange={(open) => {
          setShowBulkDeleteDialog(open);
          if (!open) {
            setBulkDeletePassword('');
            setBulkDeleteError('');
          }
        }}>
          <DialogTrigger asChild>
            <Button 
              variant="outline"
              disabled={selectedDelegates.length === 0}
              className={selectedDelegates.length > 0 ? "border-red-500 text-red-600 hover:bg-red-50" : ""}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete ({selectedDelegates.length})
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="h-5 w-5" />
                Confirm Bulk Delete
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone. All selected delegates will be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Warning */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-800 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  Warning: Permanent Deletion
                </div>
                <div className="text-red-700 text-sm mt-1">
                  You are about to delete <strong>{selectedDelegates.length}</strong> delegate{selectedDelegates.length !== 1 ? 's' : ''}.
                  This includes all their registration data, meal records, and check-in history.
                </div>
              </div>

              {/* Password confirmation */}
              <div className="space-y-2">
                <Label htmlFor="bulk-delete-password">Enter your admin password to confirm *</Label>
                <Input
                  id="bulk-delete-password"
                  type="password"
                  value={bulkDeletePassword}
                  onChange={(e) => {
                    setBulkDeletePassword(e.target.value);
                    setBulkDeleteError('');
                  }}
                  placeholder="Enter your password"
                  className={bulkDeleteError ? "border-red-500" : ""}
                />
                {bulkDeleteError && (
                  <p className="text-sm text-red-600">{bulkDeleteError}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleBulkDelete}
                disabled={!bulkDeletePassword || isBulkDeleting}
              >
                {isBulkDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete {selectedDelegates.length} Delegate{selectedDelegates.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Manual Registration Button */}
        <Dialog open={showManualRegister} onOpenChange={setShowManualRegister}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Delegate (Cash/Walk-in)
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Manual Delegate Registration
              </DialogTitle>
              <DialogDescription>
                Register a delegate who paid via cash, card, or other methods. A temporary password will be generated.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={manualForm.name}
                    onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={manualForm.email}
                    onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={manualForm.phone}
                    onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })}
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input
                    id="company"
                    value={manualForm.company}
                    onChange={(e) => setManualForm({ ...manualForm, company: e.target.value })}
                    placeholder="Acme Inc."
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Job Title</Label>
                <Input
                  id="title"
                  value={manualForm.title}
                  onChange={(e) => setManualForm({ ...manualForm, title: e.target.value })}
                  placeholder="Software Engineer"
                />
              </div>
              
              {/* Gender Selection */}
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select
                  value={manualForm.gender}
                  onValueChange={(v) => setManualForm({ ...manualForm, gender: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Ticket Selection */}
              <div className="space-y-2">
                <Label>Ticket Tier</Label>
                <Select
                  value={manualForm.tier_id}
                  onValueChange={(v) => setManualForm({ ...manualForm, tier_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free / Interest Only</SelectItem>
                    {tiers?.map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>
                        {tier.name} - ${tier.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Payment Section */}
              <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
                <h4 className="font-semibold flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Payment Details
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <Select
                      value={manualForm.payment_method}
                      onValueChange={(v) => setManualForm({ ...manualForm, payment_method: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="card">Card (Manual)</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="waived">Fee Waived</SelectItem>
                        <SelectItem value="complimentary">Complimentary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Amount Received ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={manualForm.amount_paid}
                      onChange={(e) => setManualForm({ ...manualForm, amount_paid: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                
                {/* Late Fee Section */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="late_fee"
                    checked={manualForm.apply_late_fee}
                    onCheckedChange={(checked) => setManualForm({ ...manualForm, apply_late_fee: checked })}
                  />
                  <Label htmlFor="late_fee" className="flex items-center gap-2 cursor-pointer">
                    <Clock className="h-4 w-4 text-amber-500" />
                    Apply Late Registration Fee
                    {lateSettings?.enabled && (
                      <span className="text-xs text-slate-500">
                        (Default: {lateSettings.fee_type === 'percentage' ? `${lateSettings.fee_value}%` : `$${lateSettings.fee_value}`})
                      </span>
                    )}
                  </Label>
                </div>
                
                {manualForm.apply_late_fee && (
                  <div className="space-y-2 pl-6">
                    <Label>Custom Late Fee (leave empty for default)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={manualForm.custom_late_fee || ''}
                      onChange={(e) => setManualForm({ ...manualForm, custom_late_fee: e.target.value })}
                      placeholder="Enter custom amount..."
                    />
                  </div>
                )}
                
                {/* Fee Summary */}
                <div className="border-t pt-3 mt-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Base Ticket Price:</span>
                    <span>${fees.base.toFixed(2)}</span>
                  </div>
                  {manualForm.apply_late_fee && fees.lateFee > 0 && (
                    <div className="flex justify-between text-sm text-amber-600">
                      <span>Late Registration Fee:</span>
                      <span>+${fees.lateFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-lg border-t pt-2">
                    <span>Total Due:</span>
                    <span>${fees.total.toFixed(2)}</span>
                  </div>
                  {manualForm.amount_paid > 0 && (
                    <div className={cn(
                      "flex justify-between text-sm",
                      manualForm.amount_paid >= fees.total ? "text-emerald-600" : "text-red-600"
                    )}>
                      <span>Amount Received:</span>
                      <span>${manualForm.amount_paid.toFixed(2)}</span>
                    </div>
                  )}
                  {manualForm.amount_paid > 0 && manualForm.amount_paid < fees.total && (
                    <div className="text-xs text-red-500 mt-1">
                      ⚠️ Partial payment - ${(fees.total - manualForm.amount_paid).toFixed(2)} remaining
                    </div>
                  )}
                </div>
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  value={manualForm.notes}
                  onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                  placeholder="Any additional notes about this registration..."
                  rows={2}
                />
              </div>

              {/* Receipt Number */}
              <div className="space-y-2">
                <Label>Receipt/Invoice Number (optional)</Label>
                <Input
                  value={manualForm.receipt_number}
                  onChange={(e) => setManualForm({ ...manualForm, receipt_number: e.target.value })}
                  placeholder="INV-2024-001"
                />
              </div>

              {/* Dietary Requirements */}
              <div className="space-y-2">
                <Label>Dietary Requirements (optional)</Label>
                <Input
                  value={manualForm.dietary_requirements}
                  onChange={(e) => setManualForm({ ...manualForm, dietary_requirements: e.target.value })}
                  placeholder="Vegetarian, vegan, allergies..."
                />
              </div>

              {/* Access Permissions */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <Label className="text-sm font-semibold">Access Permissions</Label>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Utensils className="h-4 w-4 text-orange-500" />
                    <span className="text-sm">Meal Access</span>
                  </div>
                  <Checkbox
                    checked={manualForm.meal_access}
                    onCheckedChange={(checked) => setManualForm({ ...manualForm, meal_access: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Accommodation Access</span>
                  </div>
                  <Checkbox
                    checked={manualForm.accommodation_access}
                    onCheckedChange={(checked) => setManualForm({ ...manualForm, accommodation_access: checked })}
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowManualRegister(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleManualRegister}
                disabled={isSubmitting || !manualForm.name || !manualForm.email}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Register Delegate
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Bulk Selection Toolbar */}
      {(selectedDelegates.length > 0 || filtered.some(d => d.status === 'interested')) && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={filtered.length > 0 && filtered.every(d => selectedDelegates.includes(d.id))}
              onCheckedChange={selectAllFiltered}
              id="select-all"
            />
            <Label htmlFor="select-all" className="text-sm cursor-pointer">
              Select All ({filtered.length})
            </Label>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={selectInterestedOnly}
            className="text-xs"
          >
            Select Interested Only
          </Button>
          {selectedDelegates.length > 0 && (
            <>
              <div className="h-4 w-px bg-slate-300" />
              <Badge variant="secondary">
                {selectedDelegates.length} selected
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDelegates([])}
                className="text-xs text-slate-500"
              >
                Clear
              </Button>
            </>
          )}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="divide-y">
            {filtered.map((delegate) => (
              <div key={delegate.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4">
                  {/* Selection Checkbox */}
                  <Checkbox
                    checked={selectedDelegates.includes(delegate.id)}
                    onCheckedChange={() => toggleDelegateSelection(delegate.id)}
                    data-testid={`select-delegate-${delegate.id}`}
                  />
                  <Avatar>
                    <AvatarFallback>
                      {delegate.name?.[0] || delegate.first_name?.[0]}{delegate.name?.split(' ')[1]?.[0] || delegate.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">{delegate.name || `${delegate.first_name} ${delegate.last_name}`}</div>
                    <div className="text-sm text-slate-500">{delegate.email}</div>
                    {delegate.company && (
                      <div className="text-sm text-slate-400">{delegate.company}</div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {delegate.registration_type === 'manual' && (
                        <Badge variant="outline" className="text-xs">Walk-in</Badge>
                      )}
                      {delegate.receipt_number && (
                        <Badge variant="outline" className="text-xs bg-blue-50">
                          <Receipt className="h-3 w-3 mr-1" />
                          {delegate.receipt_number}
                        </Badge>
                      )}
                      {(delegate.meal_access || delegate.access?.meal_access) && (
                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700">
                          <Utensils className="h-3 w-3 mr-1" />
                          Meals
                        </Badge>
                      )}
                      {(delegate.accommodation_access || delegate.housing_access || delegate.access?.accommodation_access) && (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                          <Home className="h-3 w-3 mr-1" />
                          Housing
                        </Badge>
                      )}
                      {(delegate.vip_access || delegate.access?.vip_access) && (
                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700">
                          <Star className="h-3 w-3 mr-1" />
                          VIP
                        </Badge>
                      )}
                      {(delegate.networking_access || delegate.access?.networking_access) && (
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">
                          <Users className="h-3 w-3 mr-1" />
                          Networking
                        </Badge>
                      )}
                      {(delegate.workshops_access || delegate.access?.workshops_access) && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                          <GraduationCap className="h-3 w-3 mr-1" />
                          Workshops
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge
                    variant={
                      delegate.status === "checked_in"
                        ? "default"
                        : isConfirmed(delegate.status)
                        ? "secondary"
                        : delegate.status === "interested"
                        ? "outline"
                        : "outline"
                    }
                  >
                    {delegate.status}
                  </Badge>
                  {delegate.payment?.method && (
                    <Badge variant="outline" className="text-xs">
                      {delegate.payment.method}
                    </Badge>
                  )}
                  <div className="text-sm font-medium">{delegate.coin_balance || 0} coins</div>
                  <div className="flex gap-1">
                    {/* Manual Check-In / Checked-In Status Button */}
                    {delegate.status === "checked_in" || delegate.checked_in ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-emerald-600 hover:bg-emerald-50"
                          title={`Checked in${delegate.check_in_time ? ` at ${new Date(delegate.check_in_time).toLocaleString()}` : ''}${delegate.check_in_by_name ? ` by ${delegate.check_in_by_name}` : ''}`}
                          onClick={() => setShowUndoConfirm(delegate.id)}
                          data-testid={`checkedin-delegate-${delegate.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          <span className="text-xs">Checked In</span>
                        </Button>
                        {/* Undo Check-In Dialog */}
                        {showUndoConfirm === delegate.id && (
                          <Dialog open={true} onOpenChange={() => setShowUndoConfirm(null)}>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Undo Check-In?</DialogTitle>
                                <DialogDescription>
                                  Are you sure you want to undo the check-in for <strong>{delegate.name || `${delegate.first_name} ${delegate.last_name}`}</strong>?
                                </DialogDescription>
                              </DialogHeader>
                              {delegate.check_in_time && (
                                <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded">
                                  <p>Checked in: {new Date(delegate.check_in_time).toLocaleString()}</p>
                                  {delegate.check_in_by_name && <p>By: {delegate.check_in_by_name}</p>}
                                  {delegate.check_in_gate && <p>Gate: {delegate.check_in_gate}</p>}
                                </div>
                              )}
                              <DialogFooter className="gap-2">
                                <Button variant="outline" onClick={() => setShowUndoConfirm(null)}>
                                  Cancel
                                </Button>
                                <Button
                                  variant="destructive"
                                  onClick={() => handleUndoCheckIn(delegate.id, delegate.name || `${delegate.first_name} ${delegate.last_name}`)}
                                  disabled={undoingCheckIn === delegate.id}
                                >
                                  {undoingCheckIn === delegate.id ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  ) : (
                                    <XCircle className="h-4 w-4 mr-2" />
                                  )}
                                  Undo Check-In
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        onClick={() => handleManualCheckIn(delegate.id, delegate.name || `${delegate.first_name} ${delegate.last_name}`)}
                        disabled={checkingIn === delegate.id}
                        title="Manual Check-In"
                        data-testid={`checkin-delegate-${delegate.id}`}
                      >
                        {checkingIn === delegate.id ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <UserPlus className="h-4 w-4 mr-1" />
                        )}
                        <span className="text-xs">Check In</span>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingDelegate(delegate);
                        setShowEditor(true);
                      }}
                      title="Edit Delegate"
                      data-testid={`edit-delegate-${delegate.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedDelegate(delegate);
                        setShowQR(true);
                      }}
                      title="View QR Code"
                      data-testid={`qr-delegate-${delegate.id}`}
                    >
                      <QrCode className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadBadge(delegate.id, delegate.name || delegate.first_name, delegate.last_name || '')}
                      title="Download Badge PDF"
                    >
                      <FileDown className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                No delegates found
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delegate Editor Modal */}
      <DelegateEditor
        delegate={editingDelegate}
        eventId={eventId}
        open={showEditor}
        onClose={() => {
          setShowEditor(false);
          setEditingDelegate(null);
        }}
        onSave={() => {
          queryClient.invalidateQueries({ queryKey: ['delegates', eventId] });
        }}
      />
      
      {/* QR Code Display Dialog */}
      {showQR && selectedDelegate && (
        <DelegateQRDisplay
          delegate={selectedDelegate}
          eventId={eventId}
          eventName="Event"
          showDialog={true}
          onClose={() => {
            setShowQR(false);
            setSelectedDelegate(null);
          }}
        />
      )}
    </div>
  );
};

// ============== Admin Check-In ==============
const AdminCheckIn = ({ eventId, event }) => {
  const [qrInput, setQrInput] = useState("");
  const [result, setResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [showKiosk, setShowKiosk] = useState(false);
  const queryClient = useQueryClient();

  const { data: gateStatus, refetch: refetchGate } = useQuery({
    queryKey: ["gate-status", eventId],
    queryFn: () => checkinAPI.getGateStatus(eventId).then((r) => r.data),
    refetchInterval: 5000,
  });

  if (showKiosk) {
    return (
      <KioskMode
        eventId={eventId}
        eventName={event?.name}
        onExit={() => setShowKiosk(false)}
      />
    );
  }

  const handleCheckIn = async () => {
    if (!qrInput.trim()) return;
    setIsScanning(true);
    setShowPulse(false);
    try {
      const response = await checkinAPI.checkIn(eventId, qrInput.trim());
      setResult(response.data);
      setShowPulse(true);
      
      if (response.data.status === "authorized") {
        toast.success("Check-in successful!");
        queryClient.invalidateQueries({ queryKey: ["delegates", eventId] });
        refetchGate();
      }
      
      setTimeout(() => setShowPulse(false), 2000);
    } catch (err) {
      setResult({ status: "error", reason: getErrorMessage(err, "Check-in failed") });
      setShowPulse(true);
      setTimeout(() => setShowPulse(false), 2000);
    } finally {
      setIsScanning(false);
      setQrInput("");
    }
  };

  const occupancyPercent = gateStatus?.total_capacity 
    ? Math.round((gateStatus.current_occupancy / gateStatus.total_capacity) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Gate Velocity Telemetry */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold">{gateStatus?.current_occupancy || 0}</div>
            <div className="text-sm text-slate-500">On-Site Now</div>
            <Progress value={occupancyPercent} className="mt-2 h-2" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold">{gateStatus?.total_capacity || 0}</div>
            <div className="text-sm text-slate-500">Total Capacity</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold text-blue-600">{gateStatus?.velocity?.toFixed(1) || 0}</div>
            <div className="text-sm text-slate-500">Check-ins/min</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-3xl font-bold">{(gateStatus?.total_capacity || 0) - (gateStatus?.current_occupancy || 0)}</div>
            <div className="text-sm text-slate-500">Spots Left</div>
          </CardContent>
        </Card>
      </div>

      {/* Scanner */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ScanLine className="h-5 w-5" />
                Security Control - QR Scanner
              </CardTitle>
              <CardDescription>Scan delegate QR codes for gate verification</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="default" 
                onClick={() => window.open(`/admin/kiosk/${eventId}`, '_blank')}
                data-testid="unified-kiosk-btn"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                <ScanLine className="h-4 w-4 mr-2" />
                Unified Kiosk
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open(`/admin/checkin/${eventId}`, '_blank')}
                data-testid="checkin-station-btn"
              >
                <Camera className="h-4 w-4 mr-2" />
                Check-in Only
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowKiosk(true)}
                data-testid="kiosk-mode-btn"
              >
                <Maximize className="h-4 w-4 mr-2" />
                Quick Kiosk
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Input
              placeholder="Paste or scan QR code data..."
              value={qrInput}
              onChange={(e) => setQrInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCheckIn()}
              className="flex-1 font-mono"
              data-testid="qr-input"
              autoFocus
            />
            <Button onClick={handleCheckIn} disabled={isScanning} data-testid="checkin-btn" size="lg">
              {isScanning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
            </Button>
          </div>

          {/* Signal Pulse Result Display */}
          {result && (
            <div className={cn(
              "mt-6 p-8 rounded-xl text-center transition-all duration-300",
              showPulse && "animate-pulse",
              result.status === "authorized" 
                ? "bg-emerald-50 border-4 border-emerald-500 shadow-lg shadow-emerald-200" 
                : result.status === "already_checked_in" 
                ? "bg-amber-50 border-4 border-amber-500 shadow-lg shadow-amber-200" 
                : "bg-rose-50 border-4 border-rose-500 shadow-lg shadow-rose-200"
            )}>
              {result.status === "authorized" ? (
                <>
                  <CheckCircle2 className="h-20 w-20 text-emerald-500 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-emerald-700 tracking-wide">AUTHORIZED</div>
                  <div className="text-2xl mt-3 font-medium">{result.delegate?.name}</div>
                  <div className="text-slate-600 text-lg">{result.delegate?.company}</div>
                  <Badge className="mt-3 text-lg px-4 py-1" variant="secondary">{result.delegate?.tier}</Badge>
                </>
              ) : result.status === "already_checked_in" ? (
                <>
                  <AlertCircle className="h-20 w-20 text-amber-500 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-amber-700 tracking-wide">DUPLICATE ENTRY</div>
                  <div className="text-xl mt-3">{result.delegate?.name}</div>
                  <div className="text-slate-600 mt-1">Already checked in</div>
                </>
              ) : (
                <>
                  <XCircle className="h-20 w-20 text-rose-500 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-rose-700 tracking-wide">DENIED</div>
                  <div className="text-lg mt-3 text-rose-600">{result.reason}</div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Scans */}
      {gateStatus?.recent_scans?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Scans</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {gateStatus.recent_scans.map((scan, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div className="flex items-center gap-2">
                    {scan.verification_result === "authorized" ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm">{scan.delegate_id?.slice(0, 8)}...</span>
                  </div>
                  <span className="text-xs text-slate-500">
                    {new Date(scan.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== Admin Meal Scanner ==============
const AdminMealScanner = ({ eventId }) => {
  const [qrInput, setQrInput] = useState("");
  const [day, setDay] = useState("1");
  const [category, setCategory] = useState("lunch");
  const [result, setResult] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  const handleScan = async () => {
    if (!qrInput.trim()) return;
    setIsScanning(true);
    try {
      const response = await mealAPI.scanRedeem(eventId, qrInput.trim(), parseInt(day), category);
      setResult(response.data);
      if (response.data.status === "success") {
        toast.success("Meal redeemed!");
      }
    } catch (err) {
      setResult({ status: "error", message: getErrorMessage(err, "Failed to redeem meal") });
    } finally {
      setIsScanning(false);
      setQrInput("");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Utensils className="h-5 w-5" />
            Meal Scanner
          </CardTitle>
          <CardDescription>Scan delegate badges to redeem meals</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Day</Label>
              <Select value={day} onValueChange={setDay}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((d) => (
                    <SelectItem key={d} value={d.toString()}>Day {d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Meal</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">Breakfast</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="dinner">Dinner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-4">
            <Input
              placeholder="Scan QR code..."
              value={qrInput}
              onChange={(e) => setQrInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleScan()}
              className="flex-1"
            />
            <Button onClick={handleScan} disabled={isScanning}>
              {isScanning ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
            </Button>
          </div>

          {result && (
            <Alert variant={result.status === "success" ? "default" : "destructive"}>
              {result.status === "success" ? (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>Meal Redeemed</AlertTitle>
                  <AlertDescription>
                    {result.delegate} - {result.meal}
                  </AlertDescription>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>Failed</AlertTitle>
                  <AlertDescription>{result.message}</AlertDescription>
                </>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============== Admin Branding ==============
const AdminBranding = ({ eventId, event }) => {
  const queryClient = useQueryClient();
  const [branding, setBranding] = useState(event.branding || {
    primary_color: "#3B82F6",
    secondary_color: "#1E40AF",
    accent_color: "#10B981",
    logo_url: "",
    hero_image_url: "",
    badge_background_url: "",
    favicon_url: "",
    tagline: "",
    description: "",
    social_links: {
      facebook: "",
      twitter: "",
      instagram: "",
      linkedin: "",
      youtube: ""
    }
  });
  const [isSaving, setIsSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, hero: false, badge: false, favicon: false });
  
  const logoInputRef = React.useRef(null);
  const heroInputRef = React.useRef(null);
  const badgeInputRef = React.useRef(null);
  const faviconInputRef = React.useRef(null);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await eventAPI.updateBranding(eventId, branding);
      toast.success("Branding updated!");
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
    } catch (err) {
      toast.error("Failed to update branding");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileUpload = async (file, assetType) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Use: JPEG, PNG, WebP, GIF, SVG, or ICO");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size: 5MB");
      return;
    }
    
    const uploadKey = assetType === 'logo' ? 'logo' : assetType === 'hero_image' ? 'hero' : assetType === 'favicon' ? 'favicon' : 'badge';
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    
    try {
      const response = await eventAPI.uploadBrandingAsset(eventId, assetType, file);
      // Backend returns /api/uploads/filename, construct full URL
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const newUrl = `${baseUrl}${response.data.url}`;
      
      const fieldMap = {
        'logo': 'logo_url',
        'hero_image': 'hero_image_url',
        'badge_background': 'badge_background_url',
        'favicon': 'favicon_url'
      };
      
      // Update local state
      const updatedBranding = { ...branding, [fieldMap[assetType]]: newUrl };
      setBranding(updatedBranding);
      
      // Auto-save to backend
      await eventAPI.updateBranding(eventId, updatedBranding);
      
      toast.success(`${assetType.replace('_', ' ')} uploaded and saved!`);
      queryClient.invalidateQueries({ queryKey: ["event", eventId] });
    } catch (err) {
      toast.error(getErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const updateSocialLink = (platform, value) => {
    setBranding(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value }
    }));
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    // Skip invalid test URLs
    if (url.includes('test.com') || url.includes('example.com') || url.includes('placeholder')) return null;
    if (url.startsWith('http')) return url;
    // Add /api prefix for proper routing through ingress
    return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Event Branding</CardTitle>
          <CardDescription>Customize colors and upload images - changes propagate instantly</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Colors */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  className="h-10 w-16 rounded border"
                />
                <Input
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Secondary Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={branding.secondary_color}
                  onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                  className="h-10 w-16 rounded border"
                />
                <Input
                  value={branding.secondary_color}
                  onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Accent Color</Label>
              <div className="flex gap-2 mt-1">
                <input
                  type="color"
                  value={branding.accent_color}
                  onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                  className="h-10 w-16 rounded border"
                />
                <Input
                  value={branding.accent_color}
                  onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Image Uploads */}
          <div className="space-y-6">
            {/* Logo Upload */}
            <div className="border rounded-lg p-4">
              <Label className="text-base font-medium">Logo</Label>
              <p className="text-sm text-slate-500 mb-3">Recommended: Square image, 200x200px or larger</p>
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-24 h-24 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
                  {branding.logo_url ? (
                    <img src={getImageUrl(branding.logo_url)} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Camera className="h-8 w-8 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                  />
                  <Button
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading.logo}
                    className="w-full"
                    data-testid="upload-logo-btn"
                  >
                    {uploading.logo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                    {uploading.logo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Hero Image Upload */}
            <div className="border rounded-lg p-4">
              <Label className="text-base font-medium">Hero Image</Label>
              <p className="text-sm text-slate-500 mb-3">Recommended: 1920x600px, landscape orientation</p>
              <div className="space-y-3">
                <div className="w-full h-32 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
                  {branding.hero_image_url ? (
                    <img src={getImageUrl(branding.hero_image_url)} alt="Hero" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="h-8 w-8 text-slate-400" />
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    ref={heroInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'hero_image')}
                  />
                  <Button
                    variant="outline"
                    onClick={() => heroInputRef.current?.click()}
                    disabled={uploading.hero}
                    className="flex-1"
                    data-testid="upload-hero-btn"
                  >
                    {uploading.hero ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                    {uploading.hero ? 'Uploading...' : 'Upload Hero Image'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Badge Background Upload */}
            <div className="border rounded-lg p-4">
              <Label className="text-base font-medium">Badge Background</Label>
              <p className="text-sm text-slate-500 mb-3">For printed/digital badges. Recommended: 400x600px</p>
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-20 h-28 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
                  {branding.badge_background_url ? (
                    <img src={getImageUrl(branding.badge_background_url)} alt="Badge BG" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="h-6 w-6 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={badgeInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'badge_background')}
                  />
                  <Button
                    variant="outline"
                    onClick={() => badgeInputRef.current?.click()}
                    disabled={uploading.badge}
                    className="w-full"
                    data-testid="upload-badge-btn"
                  >
                    {uploading.badge ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                    {uploading.badge ? 'Uploading...' : 'Upload Badge Background'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Favicon Upload */}
            <div className="border rounded-lg p-4">
              <Label className="text-base font-medium">Favicon</Label>
              <p className="text-sm text-slate-500 mb-3">Browser tab icon. Recommended: 32x32px ICO or PNG</p>
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
                  {branding.favicon_url ? (
                    <img src={getImageUrl(branding.favicon_url)} alt="Favicon" className="w-full h-full object-contain" />
                  ) : (
                    <Globe className="h-5 w-5 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept="image/*,.ico"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'favicon')}
                  />
                  <Button
                    variant="outline"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={uploading.favicon}
                    className="w-full"
                    data-testid="upload-favicon-btn"
                  >
                    {uploading.favicon ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Globe className="h-4 w-4 mr-2" />}
                    {uploading.favicon ? 'Uploading...' : 'Upload Favicon'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Tagline & Description */}
          <div className="space-y-4">
            <div>
              <Label>Event Tagline</Label>
              <Input
                value={branding.tagline || ""}
                onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
                placeholder="A short catchy phrase for your event..."
                className="mt-1"
                data-testid="branding-tagline"
              />
            </div>
            <div>
              <Label>Event Description</Label>
              <Textarea
                value={branding.description || ""}
                onChange={(e) => setBranding({ ...branding, description: e.target.value })}
                placeholder="Describe your event for the landing page..."
                className="mt-1"
                rows={3}
                data-testid="branding-description"
              />
            </div>
          </div>

          {/* Social Media Links */}
          <div>
            <Label className="text-base font-medium mb-3 block">Social Media Links</Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-500">Facebook</Label>
                <Input
                  value={branding.social_links?.facebook || ""}
                  onChange={(e) => updateSocialLink('facebook', e.target.value)}
                  placeholder="https://facebook.com/..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-500">Twitter/X</Label>
                <Input
                  value={branding.social_links?.twitter || ""}
                  onChange={(e) => updateSocialLink('twitter', e.target.value)}
                  placeholder="https://twitter.com/..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-500">Instagram</Label>
                <Input
                  value={branding.social_links?.instagram || ""}
                  onChange={(e) => updateSocialLink('instagram', e.target.value)}
                  placeholder="https://instagram.com/..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-500">LinkedIn</Label>
                <Input
                  value={branding.social_links?.linkedin || ""}
                  onChange={(e) => updateSocialLink('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-500">YouTube</Label>
                <Input
                  value={branding.social_links?.youtube || ""}
                  onChange={(e) => updateSocialLink('youtube', e.target.value)}
                  placeholder="https://youtube.com/..."
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Preview */}
          <div>
            <Label>Live Preview</Label>
            <div
              className="mt-2 p-6 rounded-lg text-white relative overflow-hidden"
              style={{ 
                backgroundColor: branding.primary_color,
                backgroundImage: branding.hero_image_url ? `url(${getImageUrl(branding.hero_image_url)})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-black/40" />
              <div className="relative z-10">
                {branding.logo_url && (
                  <img src={getImageUrl(branding.logo_url)} alt="Logo" className="h-12 mb-4" />
                )}
                <h3 className="text-xl font-bold">{event.name}</h3>
                <p className="opacity-80">Event branding preview</p>
              </div>
            </div>
          </div>

          <Button onClick={handleSave} disabled={isSaving} className="w-full">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Branding
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== Admin Economy ==============
const AdminEconomy = ({ eventId, delegates }) => {
  const [selectedDelegate, setSelectedDelegate] = useState(null);
  const [mintAmount, setMintAmount] = useState("");
  const [mintReason, setMintReason] = useState("");
  const [isMinting, setIsMinting] = useState(false);
  const queryClient = useQueryClient();

  const handleMint = async () => {
    if (!selectedDelegate || !mintAmount || !mintReason) return;
    setIsMinting(true);
    try {
      await adminDelegateAPI.mintCoins(eventId, selectedDelegate, parseInt(mintAmount), mintReason);
      toast.success("Coins minted!");
      queryClient.invalidateQueries({ queryKey: ["delegates", eventId] });
      setMintAmount("");
      setMintReason("");
    } catch (err) {
      toast.error("Failed to mint coins");
    } finally {
      setIsMinting(false);
    }
  };

  const totalCoins = delegates.reduce((acc, d) => acc + (d.coin_balance || 0), 0);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{totalCoins}</div>
            <div className="text-sm text-slate-500">Total Coins in Circulation</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{delegates.length}</div>
            <div className="text-sm text-slate-500">Active Wallets</div>
          </CardContent>
        </Card>
      </div>

      {/* Mint Coins */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Mint Reward Coins
          </CardTitle>
          <CardDescription>Award coins to delegates for activities</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Select Delegate</Label>
            <Select value={selectedDelegate || ""} onValueChange={setSelectedDelegate}>
              <SelectTrigger>
                <SelectValue placeholder="Choose delegate" />
              </SelectTrigger>
              <SelectContent>
                {delegates.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.first_name} {d.last_name} ({d.coin_balance} coins)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                value={mintAmount}
                onChange={(e) => setMintAmount(e.target.value)}
                placeholder="50"
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={mintReason}
                onChange={(e) => setMintReason(e.target.value)}
                placeholder="Scavenger hunt winner"
              />
            </div>
          </div>
          <Button
            onClick={handleMint}
            disabled={isMinting || !selectedDelegate || !mintAmount || !mintReason}
          >
            {isMinting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Mint Coins
          </Button>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle>Coin Leaderboard</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...delegates]
              .sort((a, b) => (b.coin_balance || 0) - (a.coin_balance || 0))
              .slice(0, 10)
              .map((d, i) => (
                <div key={d.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <div className="flex items-center gap-3">
                    <span className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                      i === 0 ? "bg-yellow-400 text-yellow-900" :
                      i === 1 ? "bg-slate-300 text-slate-700" :
                      i === 2 ? "bg-amber-600 text-white" :
                      "bg-slate-100 text-slate-600"
                    )}>
                      {i + 1}
                    </span>
                    <span>{d.first_name} {d.last_name}</span>
                  </div>
                  <span className="font-semibold">{d.coin_balance || 0}</span>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== Admin Event Page ==============
const AdminEventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  // Handle navigation to standalone pages
  const handleTabChange = (tabId) => {
    if (tabId === 'cctv') {
      navigate('/admin/cctv');
      return;
    }
    if (tabId === 'school-standalone') {
      navigate('/admin/schools');
      return;
    }
    setActiveTab(tabId);
  };

  const { data: event, isLoading } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => eventAPI.get(eventId).then((r) => r.data),
  });

  const { data: delegates } = useQuery({
    queryKey: ["delegates", eventId],
    queryFn: () => adminDelegateAPI.list(eventId).then((r) => r.data),
    enabled: !!event,
  });

  const { data: tiers } = useQuery({
    queryKey: ["tiers", eventId],
    queryFn: async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${eventId}/tiers`, {
        headers: { 'Authorization': `Bearer ${sharedGetToken()}` }
      });
      return response.json();
    },
    enabled: !!event,
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!event) {
    return (
      <DashboardLayout title="Event Not Found">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Event not found</AlertTitle>
        </Alert>
      </DashboardLayout>
    );
  }

  // Render active tab content
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <AdminEventOverview event={event} delegates={delegates || []} tiers={tiers || []} eventSlug={eventId} onTabChange={setActiveTab} />;
      case 'delegates':
        return <AdminDelegateList eventId={eventId} delegates={delegates || []} />;
      case 'tickets':
        return <TicketTierManager eventId={eventId} />;
      case 'registration-form':
        return <CustomFieldsManager eventId={eventId} />;
      case 'badge-designer':
        return <BadgeDesigner eventId={eventId} />;
      case 'checkin':
        return <AdminCheckIn eventId={eventId} event={event} />;
      case 'kiosk':
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
            <div className="text-center space-y-4">
              <h2 className="text-2xl font-bold">Unified Kiosk Mode</h2>
              <p className="text-slate-500">Open the kiosk in a new window for full-screen scanning</p>
              <Button 
                size="lg"
                onClick={() => window.open(`/admin/kiosk/${eventId}`, '_blank')}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                <ScanLine className="h-5 w-5 mr-2" />
                Launch Unified Kiosk
              </Button>
            </div>
          </div>
        );
      case 'meals':
        return (
          <Tabs defaultValue="management">
            <TabsList className="mb-4">
              <TabsTrigger value="management">Meal Management</TabsTrigger>
              <TabsTrigger value="scanner">Meal Scanner</TabsTrigger>
            </TabsList>
            <TabsContent value="management">
              <MealManagement eventId={eventId} />
            </TabsContent>
            <TabsContent value="scanner">
              <MealScanner eventId={eventId} />
            </TabsContent>
          </Tabs>
        );
      case 'meal-scanner':
        return <MealScanner eventId={eventId} />;
      case 'stock-scanner':
        return <StockScanner eventId={eventId} />;
      case 'stock-management':
        return <EventStockManagement eventId={eventId} />;
      case 'sessions':
      case 'schedule':
        return <SessionManagement eventId={eventId} />;
      case 'housing':
        return (
          <Tabs defaultValue="units">
            <TabsList className="mb-4">
              <TabsTrigger value="units">Units Management</TabsTrigger>
              <TabsTrigger value="assignment">Delegate Assignment</TabsTrigger>
            </TabsList>
            <TabsContent value="units">
              <HousingManager eventId={eventId} />
            </TabsContent>
            <TabsContent value="assignment">
              <HousingAssignment eventId={eventId} />
            </TabsContent>
          </Tabs>
        );
      case 'safety':
        return <SafetyCenter eventId={eventId} isAdmin={true} />;
      case 'security':
        return <SecurityManagement eventId={eventId} />;
      case 'campus-access':
        return <CampusAccessControl eventId={eventId} />;
      case 'vehicles':
        return <VehicleManagement eventId={eventId} />;
      case 'visitors':
        return <VisitorPassManagement eventId={eventId} />;
      case 'invites':
        return <PreRegistrationInvites eventId={eventId} />;
      case 'cameras':
        return <NetworkCameraSettings />;
      case 'branding':
        return <AdminBranding eventId={eventId} event={event} />;
      case 'economy':
        return <AdminEconomy eventId={eventId} delegates={delegates || []} />;
      case 'tasks':
        return <TaskBoard eventId={eventId} />;
      case 'scavenger':
        return <ScavengerHunt eventId={eventId} isAdmin={true} />;
      case 'bible-challenges':
        return <BibleChallengesManagement eventId={eventId} />;
      case 'bible-plans':
        return <ScopedBiblePlans scopeType="event" scopeId={eventId} isAdmin tenantName={event?.title || event?.name} />;
      case 'communications':
        return <CommunicationsHub eventId={eventId} />;
      case 'venue':
        return <VenueMaps eventId={eventId} isAdmin={true} />;
      case 'audit':
        return <AuditLog eventId={eventId} />;
      case 'accessibility':
        return <AccessibilityAdminPanel event={event} />;
      case 'research':
        return <AIResearchTool />;
      case 'ai-settings':
        return <AISettings eventId={eventId} />;
      case 'analytics':
        return <AnalyticsDashboard eventId={eventId} />;
      case 'revenue':
        return <RevenueDashboard eventId={eventId} />;
      case 'finance':
        return <FinanceManager eventId={eventId} />;
      case 'payments':
        return <PaymentManagement eventId={eventId} />;
      case 'delegate-payments':
        return <DelegatePayments eventId={eventId} />;
      case 'payment-gateways':
        return <PaymentGatewayManager eventId={eventId} />;
      case 'payment-plans':
        return <PaymentPlanManager eventId={eventId} />;
      case 'pos':
        return <POSManager eventId={eventId} />;
      case 'addons':
        return <AddOnsManager eventId={eventId} />;
      case 'delegate-access':
        return <DelegateAccessManager eventId={eventId} />;
      case 'sponsors':
        return <SponsorManagement eventId={eventId} />;
      case 'booths':
        return <ExhibitorBooths eventId={eventId} />;
      case 'speakers':
        return <SpeakerManagement eventId={eventId} />;
      case 'surveys':
        return <SurveyManagement eventId={eventId} />;
      case 'prizes':
        return <PrizeManagement eventId={eventId} />;
      case 'networking':
        return <NetworkingAppointments eventId={eventId} />;
      case 'certificates':
        return <DigitalCredentials eventId={eventId} />;
      case 'waitlist':
        return <WaitlistManagement eventId={eventId} />;
      case 'promos':
        return <PromoCodeManagement eventId={eventId} />;
      case 'comms':
        return <CommunicationsHub eventId={eventId} />;
      case 'team':
        return <TeamManagement events={[event]} />;
      case 'roles':
        return <RoleManagement />;
      case 'diagnostics':
        return <SystemDiagnostics />;
      case 'sample-data':
        return <SampleDataGenerator eventId={eventId} />;
      case 'settings':
        return <SystemSettings />;
      case 'security-dashboard':
        return <SecurityDashboard />;
      case 'feature-toggles':
        return <EventFeatureToggles eventId={eventId} />;
      case 'org-tags':
        return <EventOrgTags eventId={eventId} />;
      case 'stage':
        return <VirtualStageManager eventId={eventId} />;
      case 'livestream':
        return <EventBroadcastAdmin eventId={eventId} />;
      case 'realtime':
        return <RealtimeStats eventId={eventId} initialStats={{ total_delegates: delegates?.length || 0, checked_in: delegates?.filter(d => d.status === 'checked_in').length || 0 }} />;
      case 'live-dashboard':
        return <LiveEventDashboard eventId={eventId} />;
      case 'provincial':
        return <ProvincialDashboard eventId={eventId} />;
      case 'breakout-sessions':
        return <BreakoutSessionsManager eventId={eventId} />;
      case 'qr-health':
        return <QRHealthCheck eventId={eventId} />;
      default:
        return <AdminEventOverview event={event} delegates={delegates || []} eventSlug={eventId} onTabChange={setActiveTab} />;
    }
  };

  return (
    <AdminLayout 
      event={event} 
      activeTab={activeTab} 
      onTabChange={handleTabChange}
    >
      {renderContent()}
    </AdminLayout>
  );
};

export default AdminEventPage;
